import { useCallback, useState } from "react";
import { api } from "@/services/api";
import type { StripePaymentIntentResponse, StripePaymentStatusResponse } from "@/types/api";
import { logError } from "@/utils/logError";

type PollResult = "confirmed" | "failed" | "pending";

interface UseStripePaymentReturn {
  loading: boolean;
  createPaymentIntent: (amount: number, bookingId: string) => Promise<StripePaymentIntentResponse>;
  checkPaymentStatus: (
    bookingId: string,
    paymentIntent: string,
  ) => Promise<StripePaymentStatusResponse>;
  pollPaymentStatus: (bookingId: string, paymentIntentId: string) => Promise<PollResult>;
}

export function useStripePayment(): UseStripePaymentReturn {
  const [loading, setLoading] = useState(false);

  const createPaymentIntent = useCallback(
    async (amount: number, bookingId: string): Promise<StripePaymentIntentResponse> => {
      setLoading(true);

      try {
        const response = await api.createStripePaymentIntent(amount, bookingId);
        setLoading(false);
        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        setLoading(false);
        throw error;
      }
    },
    [],
  );

  const checkPaymentStatus = useCallback(
    async (bookingId: string, paymentIntent: string): Promise<StripePaymentStatusResponse> => {
      try {
        const response = await api.getStripePaymentStatus(bookingId, paymentIntent);
        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        throw error;
      }
    },
    [],
  );

  const pollPaymentStatus = useCallback(
    async (bookingId: string, paymentIntentId: string): Promise<PollResult> => {
      for (let attempt = 0; attempt < 10; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
          const response = await checkPaymentStatus(bookingId, paymentIntentId);
          if (response.status === "SUCCESS") return "confirmed";
          if (response.status === "FAILED") return "failed";
        } catch {
          // transient error — continue polling
        }
      }
      return "pending";
    },
    [checkPaymentStatus],
  );

  return {
    loading,
    createPaymentIntent,
    checkPaymentStatus,
    pollPaymentStatus,
  };
}
